import { defineComponent, PropType, reactive, watch } from '@vue/composition-api';
import { ConnectionStatus } from "@/views/connections/connections-types";
import { TeamtailorService, disableTeamtailor, enableTeamtailor } from '@/functions/services-functions';
import { alertDialogue, confirmDialogue, generalError } from '@/functions/system-modal-functions';
import { i18n } from "@/utilities/vue-i18n";
import { ModalIconType } from '../system-modals/system-modals';

type SettingsType = {
  connection: TeamtailorService;
  loading: boolean;
  company: number;
};

export default defineComponent({
  props: {
    connection: {
      required: true,
      type: Object as PropType<TeamtailorService>,
    },
    companyId: {
      required: false,
      type: Number,
    },
    allowDelete: {
      required: false,
      type: Boolean,
      default: false,
    },
    isAdmin: {
      required: false,
      type: Boolean,
      default: false,
    },
    loading: {
      required: false,
      type: Boolean,
      default: false,
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false,
    },
  },
  setup(props, context) {
    const settings = reactive<SettingsType>({
      connection: { ...props.connection },
      loading: props.loading,
      company: props.companyId ?? +context.root.$route.params.company,
    });

    watch(() => props.connection, (value) => settings.connection = { ...value }, { deep: true });
    watch(() => props.companyId, (value) => settings.company = value ?? +context.root.$route.params.company);
    watch(() => props.loading, (value) => settings.loading = value ?? settings.loading);

    const showIntegrationKey = async () => {
      if(props.disabled) {
        return;
      }

      await alertDialogue(
        'Teamtailor',
        i18n.t('components.teamtailor-connection.integration-key', { key: settings.connection.integrationKey }),
        ModalIconType.info
      );
    };
    const enable = async () => {
      if (settings.loading || props.disabled) {
        return;
      }

      settings.loading = true;

      const service = await enableTeamtailor(settings.company);

      settings.loading = false;
      
      if (service === null) {
        generalError(false);
        return;
      }

      settings.connection = service;

      context.emit('updated', settings.connection);

      await showIntegrationKey();
    };


    const disable = async () => {
      if (!props.allowDelete || settings.loading || props.disabled) {
        return;
      }

      settings.loading = true;

      const confirmed = await confirmDialogue(
        'Teamtailor',
        i18n.t('components.teamtailor-connection.disable-integration'),
        ModalIconType.warning
      );

      if (confirmed) {
        if (await disableTeamtailor(settings.connection.id)) {
          context.emit('updated', null);
        }
        else {
          await generalError(false);
        }
      }

      settings.loading = false;
    };

    return {
      settings,
      ConnectionStatus,
      enable,
      disable,
      showIntegrationKey,
    }
  },
})